import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * MovementsList — Asset Movements page
 *
 * Route: /inventory/movements
 *
 * Shows all physical movement records for the org.
 * Filterable by movement_type.
 * Row click → navigate to asset detail.
 *
 * APIs:
 *   GET /api/v1/inventory/movements
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Alert,
  Skeleton,
  Stack,
  TextField,
  MenuItem,
  useTheme,
  InputAdornment,
  Collapse,
  colors,
  IconButton,
  Button,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FilterListIcon from "@mui/icons-material/FilterList";
import InventoryIcon from "@mui/icons-material/Inventory";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useAuth } from "../../../../auth/useAuth";
import {
  CheckCircleOutline,
  CheckOutlined,
  Close,
  LocalShipping,
  ReplyOutlined,
  Search,
  SwapHoriz,
} from "@mui/icons-material";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

const MOVEMENT_TYPE_STYLES = {
  DISPATCH: { label: "Dispatch", color: "#1d4ed8", bg: "#eff6ff" },
  DELIVERY: { label: "Delivery", color: "#065f46", bg: "#ecfdf5" },
  RETURN: { label: "Return", color: "#92400e", bg: "#fff7ed" },
  TRANSFER: { label: "Transfer", color: "#7c3aed", bg: "#f5f3ff" },
  REDEPLOYMENT: { label: "Redeployment", color: "#0e7490", bg: "#ecfeff" },
};

const STATUS_STYLES = {
  COMPLETED: { color: "#065f46", bg: "#ecfdf5" },
  PENDING: { color: "#92400e", bg: "#fff7ed" },
  IN_TRANSIT: { color: "#1d4ed8", bg: "#eff6ff" },
};

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Box display="flex" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={{ fileName: "asset_movements" }} />
      </Box>
    </GridToolbarContainer>
  );
}

export default function MovementsList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { permissions, activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [movements, setMovements] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredMovements = movements.filter((row) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return Object.values(row).some((val) =>
      String(val ?? "")
        .toLowerCase()
        .includes(q),
    );
  });
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [typeFilter, setTypeFilter] = useState("");
  const [stats, setStats] = useState({ total: 0, loading: true });

  const hasPermission = (p) => {
    if (!permissions || permissions.size === 0) return false;
    if (permissions.has("*")) return true;
    return permissions.has(p);
  };

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const fetchMovements = useCallback(async () => {
    if (!hasPermission("inventory.movement.view")) return;
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...(typeFilter && { movement_type: typeFilter }),
      };
      const res = await axios.get(`${API_BASE}/movements/${activeOrg.id}`, {
        headers: authHeaders,
        params,
      });
      setMovements(res.data.data || []);
      setRowCount(res.data.meta?.pagination?.total_items || 0);
      setError(null);
    } catch {
      setError("Failed to load movements.");
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, paginationModel, typeFilter]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  // Stats
  useEffect(() => {
    if (!hasPermission("inventory.movement.view")) return;
    axios
      .get(`${API_BASE}/movements/${activeOrg.id}`, {
        headers: authHeaders,
        params: { per_page: 1 },
      })
      .then((res) =>
        setStats({
          total: res.data.meta?.pagination?.total_items ?? 0,
          loading: false,
        }),
      )
      .catch(() => setStats((prev) => ({ ...prev, loading: false })));
  }, [token, activeOrg]);

  const columns = [
    {
      field: "serialIndex",
      headerName: "S.No",
      width: 64,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {paginationModel.page * paginationModel.pageSize +
              params.api.getRowIndexRelativeToVisibleRows(params.id) +
              1}
          </Typography>
        </Box>
      ),
    },
    {
      field: "movement_type",
      headerName: "Type",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const s = MOVEMENT_TYPE_STYLES[params.value] || {
          label: params.value,
          color: "#374151",
          bg: "#f3f4f6",
        };
        return (
          <Chip
            label={s.label}
            size="small"
            sx={{
              bgcolor: s.bg,
              color: s.color,
              fontWeight: 600,
              borderRadius: "6px",
              fontSize: 11,
            }}
          />
        );
      },
    },
    {
      field: "inventory_asset_id",
      headerName: "Asset",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color="primary.main"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {params.row.asset_serial_number || params.value?.slice(0, 8) + "…"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "from_location_name",
      headerName: "From",
      flex: 1.1,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {params.value
              ? `${params.value}${params.row.from_location_city ? ` · ${params.row.from_location_city}` : ""}`
              : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "to_location_name",
      headerName: "To",
      flex: 1.1,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {params.value
              ? `${params.value}${params.row.to_location_city ? ` · ${params.row.to_location_city}` : ""}`
              : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "movement_status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => {
        const s = STATUS_STYLES[params.value] || {
          color: "#374151",
          bg: "#f3f4f6",
        };
        return (
          <Chip
            label={params.value?.replace(/_/g, " ")}
            size="small"
            sx={{
              bgcolor: s.bg,
              color: s.color,
              fontWeight: 600,
              borderRadius: "6px",
              fontSize: 10,
            }}
          />
        );
      },
    },
    {
      field: "courier_name",
      headerName: "Courier",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "moved_at",
      headerName: "Moved at",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ py: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {params.value
              ? new Date(params.value).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Typography>
        </Box>
      ),
    },
  ];

  if (!hasPermission("inventory.movement.view")) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          You do not have permission to view asset movements.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        margin: "0 auto",
        background:
          "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
        p: { xs: 2, md: 3 },
      }}
    >
      {/* header */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 3,
          px: { xs: 2, md: 3 },
          py: { xs: 2.5, md: 3 },
          borderRadius: 5,
          border: "1px solid rgba(148, 163, 184, 0.18)",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          boxShadow: "0 30px 80px rgba(15, 23, 42, 0.18)",
        }}
      >
        <Box
          sx={{
            mb: 4,
          }}
        >
         <Typography variant="h4" fontWeight={700} sx={{ color: "#e2f8f5" ,fontSize:{xs:28,md:40} }}>
            Asset Movements
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              maxWidth: 580,
              color: "rgba(226, 232, 240, 0.86)",
              fontSize: { xs: 14, md: 16 },
              lineHeight: 1.7,
            }}
          >
            Physical logistics history across all assets.
          </Typography>
        </Box>
        {/* Stat card */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {[
            {
              label: "Total movements",
              value: stats.total,
              colors: "#ba99f6",
              bg: "#f5f3ff",
              icon: <SwapHoriz />,
            },
            {
              label: "Dispatch",
              value: stats.completed,
              colors: "#6689e6",
              bg: "#eff6ff",
              icon: <CheckOutlined />,
            },
            {
              label: "Dilivery",
              value: stats.pending,
              colors: "#418773",
              bg: "#ecfdf5",
              icon: <LocalShipping />,
            },
            {
              label: "Return",
              value: stats.cancelled,
              colors: "#f7b882",
              bg: "#fff7ed",
              icon: <ReplyOutlined />,
            },
          ].map((item, i) => (
            <Card
              key={i}
              elevation={0}
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 16px)",
                  md: "1 1 calc(33.33% - 16px)",
                  lg: "1 1 calc(16.66% - 16px)",
                },
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.08)",
                p: 2,
                display: "flex",
                alignItems: "center",

                background: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s ease",

                "&:hover": {
                  // transform: "translateY(-6px)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
                  background: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <Box display="flex" alignItems="center" width="100%">
                {/* ICON */}
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "14px",
                    mr: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",

                    background: item.bg,
                    transition: "0.3s",
                  }}
                >
                  {React.cloneElement(item.icon, {
                    sx: { color: item.colors, fontSize: 24 },
                  })}
                </Box>

                {/* TEXT */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(226,232,240,0.7)",
                      fontWeight: 500,
                    }}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: "#f1f5f9",
                    }}
                  >
                    {item.value?.toLocaleString() ?? "0"}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Filter bar */}
        <Box
          p={2}
          borderBottom="1px solid rgba(148, 163, 184, 0.18)"
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          sx={{
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          <TextField
            select
            size="small"
            label="Movement type"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", sm: 200 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: "rgba(203, 213, 225, 0.60)" }}
                  />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All types</MenuItem>
            {Object.entries(MOVEMENT_TYPE_STYLES).map(([v, s]) => (
              <MenuItem key={v} value={v}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>

          {typeFilter && (
            <Button
              size="small"
              onClick={() => {
                setTypeFilter("");
                setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              sx={{
                textTransform: "none",
                fontSize: 12,
                height: 38,
                color: "#fca5a5",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                background: "rgba(239, 68, 68, 0.10)",
                borderRadius: 1,
                "&:hover": {
                  borderColor: "rgba(239, 68, 68, 0.55)",
                  background: "rgba(239, 68, 68, 0.18)",
                },
              }}
            >
              Clear filter
            </Button>
          )}

          {/* Search bar — right side */}
          <TextField
            size="small"
            placeholder="Search movements..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", sm: 260 },
              ml: { xs: 0, sm: "auto" },
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "rgba(148, 163, 184, 0.55)",
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: "rgba(203, 213, 225, 0.60)" }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery("");
                      setPaginationModel((p) => ({ ...p, page: 0 }));
                    }}
                    sx={{
                      color: "rgba(203, 213, 225, 0.60)",
                      p: 0.3,
                      "&:hover": {
                        color: "#e2f8f5",
                        background: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        <Box
          sx={{
            height: movements.length === 0 && !loading ? 320 : 580,
            width: "100%",
          }}
        >
          <DataGrid
            rows={filteredMovements}
            rowCount={rowCount}
            loading={loading}
            paginationMode="server"
            pageSizeOptions={[15, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            columns={columns}
            getRowId={(row) => row.id}
            onRowClick={(params) =>
              navigate(`/inventory/assets/${params.row.inventory_asset_id}`)
            }
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  py={6}
                >
                  <LocalShippingIcon
                    sx={{ fontSize: 52, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                    gutterBottom
                  >
                    {typeFilter
                      ? "No movements match this filter"
                      : "No movements recorded yet"}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Log movements from the asset detail page
                  </Typography>
                </Box>
              ),
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "text.secondary",
                fontSize: 12,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#334155" : "#f8fafc",
                cursor: "pointer",
              },
              "& .MuiDataGrid-cell:focus": { outline: "none" },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
}
